import * as React from 'react';
import { StatusChip, type StatusChipTone } from './status-chip';
import { cn } from '@/lib/utils/cn';

/**
 * Severity chip — the one place a record's severity is drawn (C2-55).
 *
 * Shows the severity word itself (Critical / High / Medium / Low / Info),
 * never a posture: posture is the engagement-wide rollup (R6 invariant),
 * severity is a property of one finding, risk or issue. The chip tone is
 * the `StatusChip` tone the word belongs to and the dot is the E22
 * severity ramp (`--severity-{critical,high,medium,low}`), so High and
 * Critical read apart even though both are red-tinted.
 *
 *   critical → critical tint, darkest dot
 *   high     → critical tint
 *   medium   → at-risk tint
 *   low      → neutral, lightest dot
 *   info     → neutral
 *   (none)   → an em dash, like the other empty cells
 *   other    → neutral chip with the stored word (an older AI row may
 *              carry a word outside the list)
 *
 * Words are matched without regard to case, so the findings vocabulary
 * (critical/high/medium/low/info) and the RAID form's (high/medium/low)
 * both land here.
 */
export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

export const SEVERITY_LEVELS: readonly SeverityLevel[] = [
  'critical',
  'high',
  'medium',
  'low',
  'info',
];

export const SEVERITY_LABEL: Record<SeverityLevel, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
};

const SEVERITY_TONE: Record<SeverityLevel, StatusChipTone> = {
  critical: 'critical',
  high: 'critical',
  medium: 'at-risk',
  low: 'neutral',
  info: 'neutral',
};

const SEVERITY_DOT: Record<SeverityLevel, string> = {
  critical: 'bg-severity-critical',
  high: 'bg-severity-high',
  medium: 'bg-severity-medium',
  low: 'bg-severity-low',
  info: 'bg-posture-unassessed',
};

/** The severity level a stored word names, whatever its case; null when it is not in the list. */
export function normalizeSeverity(value: unknown): SeverityLevel | null {
  if (typeof value !== 'string') return null;
  const key = value.trim().toLowerCase();
  return (SEVERITY_LEVELS as readonly string[]).includes(key) ? (key as SeverityLevel) : null;
}

export interface SeverityChipProps {
  /** The stored severity word; null, undefined or blank draws an em dash. */
  severity: string | null | undefined;
  size?: 'sm' | 'md';
  className?: string;
}

export function SeverityChip({ severity, size = 'md', className }: SeverityChipProps) {
  const raw = typeof severity === 'string' ? severity.trim() : '';
  if (raw.length === 0) {
    return (
      <span
        aria-label="Severity: not set"
        data-severity=""
        className={cn('text-body-sm text-ink-500', className)}
      >
        —
      </span>
    );
  }
  const level = normalizeSeverity(raw);
  if (!level) {
    return (
      <StatusChip
        tone="neutral"
        size={size}
        title="Severity outside the list"
        className={className}
      >
        <span data-severity={raw}>{raw}</span>
      </StatusChip>
    );
  }
  return (
    <StatusChip tone={SEVERITY_TONE[level]} size={size} className={className}>
      <span
        aria-hidden="true"
        className={cn('inline-block h-1.5 w-1.5 flex-none rounded-full', SEVERITY_DOT[level])}
      />
      <span data-severity={level}>{SEVERITY_LABEL[level]}</span>
    </StatusChip>
  );
}
